<template>
  <div class="html-material">
    <div class="material-controls">
      <div class="container visible-pc-only">
        <div class="lesson-description">
          <p class="rank">{{ pcRankDescription }}</p>
          <p class="lesson">{{ pcLessonDescription }}</p>
        </div>
        <MaterialControlsZoom />
        <div :class="{ 'one-button': hideDownloadButton }">
          <el-button @click="print" plain>{{ $t('htmlMaterial.print') }}</el-button>
          <el-button @click="downloadHtml" v-if="!hideDownloadButton" plain>
            {{ $t('htmlMaterial.downloadHtml') }}
          </el-button>
        </div>
      </div>

      <div class="container visible-sp-only">
        <p>{{ $t('htmlMaterial.title', [getProgramName()]) }}</p>
        <el-button @click="downloadHtml" v-if="!hideDownloadButton">
          {{ $t('htmlMaterial.downloadHtml') }}
        </el-button>
      </div>
    </div>

    <div class="lesson-info visible-sp-only">
      <p class="rank">{{ spLevelRank }}</p>
      <div class="lesson-title">
        <p>Lesson {{ htmlLessonNo }}</p>
        <p class="text-bold">{{ htmlTitle }}</p>
      </div>
    </div>
    <div :class="['html-wrapper', { revamp: htmlIsRevamp }]">
      <perfect-scrollbar
        class="html-inner zoom-active"
        :style="mobileHeight"
        v-dragscroll="true"
        ref="materialScroll"
      >
        <pinch-zoom
          :class="['pinch-wrapper']"
          :style="materialWidth"
          :disabled="!isEnabledPinchZoom"
          limitZoom="3"
          ref="pinchZoom"
        >
          <div class="content-wrapper">
            <div v-html="cssContent" />
            <div
              class="html-content"
              :style="!htmlIsRevamp && zoomPdfHtmlMaterial"
              v-dompurify-html="htmlBody"
            />
          </div>
        </pinch-zoom>
      </perfect-scrollbar>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex';
import { dragscroll } from 'vue-dragscroll';
import UAParser from 'ua-parser-js';
import PinchZoom from 'vue-pinch-zoom';

import MaterialControlsZoom from '@/components/Lesson/Material/MaterialControlsZoom';
import materialMixin from '@/mixins/material';

export default {
  directives: { dragscroll },
  components: {
    MaterialControlsZoom,
    PinchZoom
  },
  mixins: [materialMixin],
  data() {
    return {
      //
    };
  },
  mounted() {
    this.setMaterialLevel();
    this.initializeRevamp();
    this.setTablePerfectScroll();
    this.showHtmlContent(true);
  },
  computed: {
    ...mapState('material', ['pdfHtmlZoom']),
    ...mapState('htmlMaterial', [
      'htmlCss',
      'htmlBody',
      'htmlLevelName',
      'htmlLevel',
      'htmlRank',
      'htmlLessonId',
      'htmlTitle',
      'htmlLessonNo',
      'htmlPdfUrl',
      'htmlUrl',
      'htmlIsRevamp',
      'htmlHasPrefix'
    ]),
    ...mapGetters('htmlMaterial', ['isStudent', 'isB2B2C']),

    cssContent() {
      return `<style type="text/css">${this.htmlCss}</style>`;
    },

    zoomIcon() {
      const icon = this.isMaterialFullScreen ? 'text_zoomout_sp@2x.png' : 'text_zoom_sp@2x.png';
      return require(`@/assets/img/${icon}`);
    },

    materialWidth() {
      document.documentElement.style.setProperty('--html-wrapper-width', this.htmlAdjustedWidth + 'px');

      const pdfHtmlWidth = this.htmlAdjustedWidth * this.pdfHtmlZoom;
      return { width: pdfHtmlWidth + 'px' };
    },

    htmlAdjustedWidth() {
      // set a fix width on revamp as well due to possible new styling that might affect the width
      if (this.htmlIsRevamp) {
        const revampWidth = 892.75;
        return revampWidth * this.scalePercentage;
      }

      // set manual width on legacy materials due to different output from coding
      const htmlLegacyWidths = [
        {
          levels: [1, 4, 5, 21, 22],
          width: 892.75
        },
        {
          levels: [2, 3],
          width: 810
        },
        {
          levels: [6],
          width: 604
        }
      ];
      return htmlLegacyWidths.find((key) => key.levels.includes(this.htmlLevel))?.width;
    },

    isEnabledPinchZoom() {
      const deviceType = new UAParser().getDevice().type;
      return ['mobile', 'tablet'].includes(deviceType);
    },

    pcRankDescription() {
      return this.formatHtmlMaterialRankLabel(
        this.htmlLevelName,
        this.htmlRank,
        this.htmlIsRevamp,
        this.isStudent
      );
    },

    pcLessonDescription() {
      return this.formatHtmlMaterialLessonLabel(this.htmlLessonNo, this.htmlTitle);
    },

    spLevelRank() {
      return `${this.htmlLevelName} ${this.htmlRank}`;
    },

    hideDownloadButton() {
      return this.isCorpMaterial && this.isB2B2C;
    }
  },
  methods: {
    ...mapMutations('material', ['showHtmlContent']),

    setMaterialLevel() {
      const htmlMaterial = document.querySelector('.html-lesson-wrapper');
      if (htmlMaterial) {
        htmlMaterial.classList.add(`level-${this.htmlLevel}`);
        htmlMaterial.classList.add(`lesson-${this.htmlLessonId}`);
      }
    },

    print() {
      document.title = this.spLevelRank;
      window.print();
    },

    downloadHtml() {
      fetch(this.htmlUrl)
        .then((response) => response.text())
        .then((html) => {
          let htmlObject = document.implementation.createHTMLDocument();
          htmlObject.open();
          htmlObject.write(html);
          htmlObject.close();

          // remove inline import of style
          let linkStyle = htmlObject.querySelector('link[rel=stylesheet][href*="main"]');
          if (linkStyle) {
            linkStyle.parentNode.removeChild(linkStyle);
          }

          // append css internally to html
          let style = htmlObject.createElement('style');
          style.textContent = this.htmlCss;
          htmlObject.head.appendChild(style);

          // get html contents and place into a blob
          const blob = new Blob([htmlObject.documentElement.innerHTML], {
            type: 'text/html'
          });

          // convert to downloadable url html
          const docUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          const fileName = `${this.htmlLevel}-${this.htmlRank.slice(-1)}-${this.htmlLessonNo}`;
          link.setAttribute('download', fileName + '.html');
          link.setAttribute('href', URL.createObjectURL(blob));
          link.click();

          window.URL.revokeObjectURL(docUrl);
        });
    }
  }
};
</script>

<style src="@/assets/css/pc/html_material.css"></style>
<style scoped src="@/assets/css/pc/html_printing.css"></style>
<style scoped src="@/assets/css/sp/html_material.css"></style>
<style scoped>
.html-content {
  transform-origin: top left;
  background-color: #ffffff;
}
.revamp .html-content {
  transform: scale(var(--revamp-scale));
  width: calc(100% + 74%);
  background: none;
}
@media screen and (max-width: 959px) {
  .content-wrapper {
    width: 100vw !important;
  }
  .html-wrapper.revamp {
    width: 100vw !important;
  }
  .revamp .html-content {
    transform: unset;
    width: 100%;
    margin-right: 0px !important;
    margin-bottom: 0px !important;
  }
}
</style>
